import { vocabularyLevelChecker } from '@/lib/vocabularyLevel';
import { type VocabularyLookupResult } from '@/types/vocabulary';

interface LookupApiResponse {
  success: boolean;
  data?: VocabularyLookupResult;
}

const resultCache = new Map<string, VocabularyLookupResult | null>();
const inflightMap = new Map<string, Promise<VocabularyLookupResult | null>>();

function normalizeKey(word: string): string | null {
  if (typeof word !== 'string') {
    return null;
  }
  const normalized = word.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

function buildLocalResult(key: string): VocabularyLookupResult | null {
  try {
    if (!vocabularyLevelChecker.hasTranslation(key)) {
      return null;
    }

    const translations = vocabularyLevelChecker.getTranslation(key);
    const usphone = vocabularyLevelChecker.getUSPronunciation(key);

    const translationText = (() => {
      if (Array.isArray(translations)) {
        return translations.join('; ');
      }
      if (typeof translations === 'string') {
        return translations;
      }
      if (translations != null) {
        return String(translations);
      }
      return '';
    })().trim();

    return {
      word: key,
      translation: translationText || undefined,
      phonetic: usphone || undefined,
    };
  } catch (error) {
    console.warn('Local vocabulary lookup (sync) failed:', error);
    return null;
  }
}

async function tryLocalLookup(key: string): Promise<VocabularyLookupResult | null> {
  const immediate = buildLocalResult(key);
  if (immediate) {
    return immediate;
  }

  try {
    await vocabularyLevelChecker.hasTranslationAsync(key);
    return buildLocalResult(key);
  } catch (error) {
    console.warn('Local vocabulary lookup (async) failed:', error);
    return null;
  }
}

export async function lookupVocabulary(word: string): Promise<VocabularyLookupResult | null> {
  const key = normalizeKey(word);
  if (!key) {
    return null;
  }

  if (resultCache.has(key)) {
    return resultCache.get(key) ?? null;
  }

  const localResult = await tryLocalLookup(key);
  if (localResult) {
    resultCache.set(key, localResult);
    return localResult;
  }

  const inflight = inflightMap.get(key);
  if (inflight) {
    return inflight;
  }

  const request = (async (): Promise<VocabularyLookupResult | null> => {
    try {
      const response = await fetch(`/api/vocabulary/lookup?word=${encodeURIComponent(key)}`);
      if (!response.ok) {
        resultCache.set(key, null);
        return null;
      }

      const data = (await response.json()) as LookupApiResponse;
      if (data?.success && data.data) {
        resultCache.set(key, data.data);
        return data.data;
      }

      resultCache.set(key, null);
      return null;
    } catch (error) {
      console.warn('Vocabulary lookup request failed:', error);
      resultCache.set(key, null);
      return null;
    } finally {
      inflightMap.delete(key);
    }
  })();

  inflightMap.set(key, request);
  return request;
}

export function primeVocabularyCache(word: string, result: VocabularyLookupResult | null): void {
  const key = normalizeKey(word);
  if (!key) {
    return;
  }
  resultCache.set(key, result);
}

export function clearVocabularyLookupCache(): void {
  resultCache.clear();
  inflightMap.clear();
}
